import type { FunctionComponent } from 'react';
import React, { useMemo } from 'react';

import { canEdit } from '@trello/business-logic/board';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { forTemplate } from '@trello/i18n';
import { CalendarIcon } from '@trello/nachos/icons/calendar';
import { DashboardIcon } from '@trello/nachos/icons/dashboard';
import type { GlyphProps } from '@trello/nachos/icons/list';
import { ListIcon } from '@trello/nachos/icons/list';
import { LocationIcon } from '@trello/nachos/icons/location';
import { TableIcon } from '@trello/nachos/icons/table';
import { TimelineIcon } from '@trello/nachos/icons/timeline';
import { Spinner } from '@trello/nachos/spinner';
import { memberId } from '@trello/session-cookie';

import { ViewType } from 'app/src/components/ViewsGenerics';
import { useCurrentView } from './useCurrentView';
import type { ViewSwitcherBoardQuery } from './ViewSwitcherBoardQuery.generated';
import { useViewSwitcherBoardQuery } from './ViewSwitcherBoardQuery.generated';
import { ViewSwitcherButtons } from './ViewSwitcherButtons';
import { useViewSwitcherMemberQuery } from './ViewSwitcherMemberQuery.generated';

import styles from './ViewSwitcher.less';

const format = forTemplate('view_switcher');

export type SwitcherView = Pick<
  NonNullable<
    NonNullable<ViewSwitcherBoardQuery['board']>['prefs']
  >['switcherViews'][0],
  'enabled' | 'viewType'
>;

export type SwitcherView_ViewType = SwitcherView['viewType'];

export interface ViewOption {
  name: SwitcherView_ViewType;
  localizedName: string;
  icon: FunctionComponent<GlyphProps>;
}

export const ViewTypesMapping: Record<ViewType, SwitcherView_ViewType> = {
  board: 'Board',
  table: 'Table',
  calendar: 'Calendar',
  map: 'Map',
  dashboard: 'Dashboard',
  timeline: 'Timeline',
};

export const viewOptions: {
  [key in SwitcherView_ViewType]: ViewOption;
} = {
  Board: {
    name: ViewTypesMapping[ViewType.BOARD],
    localizedName: format('Board'),
    icon: ListIcon,
  },
  Table: {
    name: ViewTypesMapping[ViewType.TABLE],
    localizedName: format('Table'),
    icon: TableIcon,
  },
  Calendar: {
    name: ViewTypesMapping[ViewType.CALENDAR],
    localizedName: format('Calendar'),
    icon: CalendarIcon,
  },
  Timeline: {
    name: ViewTypesMapping[ViewType.TIMELINE],
    localizedName: format('Timeline'),
    icon: TimelineIcon,
  },
  Dashboard: {
    name: ViewTypesMapping[ViewType.DASHBOARD],
    localizedName: format('Dashboard'),
    icon: DashboardIcon,
  },
  Map: {
    name: ViewTypesMapping[ViewType.MAP],
    localizedName: format('Map'),
    icon: LocationIcon,
  },
};

export interface ViewSwitcherProps {
  idBoard: string;
}

export const ViewSwitcher: FunctionComponent<ViewSwitcherProps> = ({
  idBoard,
}) => {
  const currentView = useCurrentView();

  const currentViewBoardPrefs = currentView
    ? ViewTypesMapping[currentView]
    : null;

  const {
    data: memberData,
    loading: memberDataLoading,
  } = useViewSwitcherMemberQuery(
    memberId
      ? {
          variables: { memberId },
          waitOn: ['MemberHeader'],
          fetchPolicy: 'cache-only',
        }
      : { skip: true, waitOn: ['None'] },
  );

  const {
    data: boardData,
    loading: boardDataLoading,
  } = useViewSwitcherBoardQuery({
    variables: { boardId: idBoard },
    waitOn: ['CurrentBoardFull'],
    fetchPolicy: 'cache-only',
  });

  const member = memberData?.member;
  const board = boardData?.board;
  const organization = board?.organization || null;

  const loading = boardDataLoading || memberDataLoading;

  const hasViewsFeature = useMemo(
    () => board?.premiumFeatures?.includes('views') ?? false,
    [board?.premiumFeatures],
  );

  const memberCanEditBoard = Boolean(
    board &&
      member &&
      organization &&
      canEdit(member, board, organization, null),
  );

  const defaultSwitcherViews: SwitcherView[] = [
    ViewType.BOARD,
    ViewType.TABLE,
    ViewType.CALENDAR,
    ViewType.TIMELINE,
    ViewType.DASHBOARD,
    ViewType.MAP,
  ].map((viewType) => ({
    enabled: viewType === ViewType.BOARD,
    viewType: ViewTypesMapping[viewType],
  }));

  const headerRewriteEnabled =
    useFeatureFlag<'stable' | 'alpha'>(
      'panorama.board-header-rewrite',
      'stable',
    ) === 'alpha';

  const switcherViews = useMemo(() => {
    if (hasViewsFeature && board?.prefs?.switcherViews) {
      return board?.prefs?.switcherViews;
    } else {
      return defaultSwitcherViews;
    }
  }, [hasViewsFeature, board?.prefs?.switcherViews, defaultSwitcherViews]);

  const analyticsContainers = useMemo(() => {
    return {
      board: {
        id: idBoard,
      },
      organization: {
        id: board?.idOrganization,
      },
      workspace: {
        id: board?.idOrganization,
      },
      enterprise: {
        id: board?.idEnterprise,
      },
    };
  }, [board?.idEnterprise, board?.idOrganization, idBoard]);

  const switcherButtons = useMemo(
    () => (
      <ViewSwitcherButtons
        switcherViews={switcherViews}
        viewOptions={viewOptions}
        currentView={currentViewBoardPrefs}
        hasViewsFeature={hasViewsFeature}
        memberCanEditBoard={memberCanEditBoard}
        idBoard={idBoard}
        analyticsContainers={analyticsContainers}
      />
    ),
    [
      analyticsContainers,
      currentViewBoardPrefs,
      hasViewsFeature,
      idBoard,
      memberCanEditBoard,
      switcherViews,
    ],
  );

  if (loading) {
    return <Spinner />;
  }

  // TODO This is a temporary override. There is a margin on this component for
  // workflowers.modernization-update, but ideally this component should have 0
  // margin by default.
  const overrideMargin = headerRewriteEnabled ? { margin: '0' } : {};

  return (
    <div style={overrideMargin} className={styles.viewSwitcherContainer}>
      {switcherButtons}
    </div>
  );
};
